import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

export type SignaturePadHandle = {
  clear: () => void;
  isEmpty: () => boolean;
  toBlob: () => Promise<Blob>;
};

type SignaturePadProps = {
  label: string;
  valueDataUrl: string;
  onChange: (value: string) => void;
  error?: string;
};

type Point = { x: number; y: number };

const LINE_WIDTH = 3.5;
const STROKE_COLOR = "#111827";

function getPixelRatio() {
  return Math.max(window.devicePixelRatio || 1, 1);
}

function fillBackground(canvas: HTMLCanvasElement, context: CanvasRenderingContext2D) {
  const ratio = getPixelRatio();
  const width = canvas.width / ratio;
  const height = canvas.height / ratio;
  context.clearRect(0, 0, width, height);
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, width, height);
}

function drawImageToCanvas(
  canvas: HTMLCanvasElement,
  context: CanvasRenderingContext2D,
  imageSource: string,
  isStillCurrent: () => boolean,
) {
  const image = new Image();
  image.onload = () => {
    // Se nel frattempo è iniziato un nuovo tratto, non sovrascrivere il canvas.
    if (!isStillCurrent()) {
      return;
    }

    const ratio = getPixelRatio();
    fillBackground(canvas, context);
    context.drawImage(image, 0, 0, canvas.width / ratio, canvas.height / ratio);
  };
  image.src = imageSource;
}

function midpoint(a: Point, b: Point): Point {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

export const SignaturePad = forwardRef<SignaturePadHandle, SignaturePadProps>(
  function SignaturePad({ label, valueDataUrl, onChange, error }, ref) {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const contextRef = useRef<CanvasRenderingContext2D | null>(null);
    const activePointerIdRef = useRef<number | null>(null);
    const rectRef = useRef<DOMRect | null>(null);
    const pointsRef = useRef<Point[]>([]);
    const strokeCountRef = useRef(0);
    const hasInkRef = useRef(Boolean(valueDataUrl));
    const currentDataUrlRef = useRef(valueDataUrl);
    const sizeRef = useRef({ width: 0, height: 0 });
    const [isReady, setIsReady] = useState(false);
    const [showPlaceholder, setShowPlaceholder] = useState(!valueDataUrl);

    useEffect(() => {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (!canvas || !container) {
        return undefined;
      }

      function configureCanvas() {
        if (!canvas || !container) {
          return;
        }

        const width = Math.floor(container.clientWidth);
        const height = Math.floor(container.clientHeight);
        if (
          width === 0 ||
          height === 0 ||
          (width === sizeRef.current.width && height === sizeRef.current.height)
        ) {
          return;
        }

        sizeRef.current = { width, height };
        const ratio = getPixelRatio();
        canvas.width = width * ratio;
        canvas.height = height * ratio;
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;

        const context = canvas.getContext("2d");
        if (!context) {
          return;
        }

        context.setTransform(ratio, 0, 0, ratio, 0, 0);
        context.lineCap = "round";
        context.lineJoin = "round";
        context.lineWidth = LINE_WIDTH;
        context.strokeStyle = STROKE_COLOR;
        context.fillStyle = STROKE_COLOR;
        contextRef.current = context;
        fillBackground(canvas, context);

        if (currentDataUrlRef.current) {
          const strokeCount = strokeCountRef.current;
          drawImageToCanvas(
            canvas,
            context,
            currentDataUrlRef.current,
            () => strokeCountRef.current === strokeCount,
          );
        }

        setIsReady(true);
      }

      configureCanvas();
      const observer = new ResizeObserver(() => configureCanvas());
      observer.observe(container);

      return () => {
        observer.disconnect();
      };
    }, []);

    useEffect(() => {
      // Il valore appena prodotto da questo stesso canvas non va ridisegnato:
      // il ridisegno asincrono cancellerebbe l'inizio del tratto successivo.
      if (valueDataUrl === currentDataUrlRef.current) {
        return;
      }

      currentDataUrlRef.current = valueDataUrl;
      hasInkRef.current = Boolean(valueDataUrl);
      setShowPlaceholder(!valueDataUrl);

      const canvas = canvasRef.current;
      const context = contextRef.current;
      if (!canvas || !context || !isReady) {
        return;
      }

      fillBackground(canvas, context);
      if (valueDataUrl) {
        const strokeCount = strokeCountRef.current;
        drawImageToCanvas(
          canvas,
          context,
          valueDataUrl,
          () => strokeCountRef.current === strokeCount,
        );
      }
    }, [isReady, valueDataUrl]);

    function toCanvasPoint(event: { clientX: number; clientY: number }): Point {
      const rectangle = rectRef.current ?? canvasRef.current?.getBoundingClientRect();
      if (!rectangle) {
        return { x: 0, y: 0 };
      }

      return {
        x: event.clientX - rectangle.left,
        y: event.clientY - rectangle.top,
      };
    }

    function drawDot(context: CanvasRenderingContext2D, point: Point) {
      context.beginPath();
      context.arc(point.x, point.y, LINE_WIDTH / 2, 0, Math.PI * 2);
      context.fill();
    }

    function addPoint(point: Point) {
      const context = contextRef.current;
      if (!context) {
        return;
      }

      const points = pointsRef.current;
      points.push(point);

      if (points.length === 2) {
        const end = midpoint(points[0], points[1]);
        context.beginPath();
        context.moveTo(points[0].x, points[0].y);
        context.lineTo(end.x, end.y);
        context.stroke();
        return;
      }

      // Curva quadratica tra i punti medi: tratto morbido anche con pochi campioni.
      const [previous, control, current] = points.slice(-3);
      const start = midpoint(previous, control);
      const end = midpoint(control, current);
      context.beginPath();
      context.moveTo(start.x, start.y);
      context.quadraticCurveTo(control.x, control.y, end.x, end.y);
      context.stroke();
      pointsRef.current = points.slice(-3);
    }

    function commitSignature() {
      const canvas = canvasRef.current;
      if (!canvas) {
        return;
      }

      currentDataUrlRef.current = canvas.toDataURL("image/png");
      onChange(currentDataUrlRef.current);
    }

    function handlePointerDown(event: React.PointerEvent<HTMLCanvasElement>) {
      const canvas = canvasRef.current;
      const context = contextRef.current;
      // Un solo dito/penna alla volta: ignora palmo e tocchi secondari.
      if (!canvas || !context || !event.isPrimary || activePointerIdRef.current !== null) {
        return;
      }

      event.preventDefault();
      canvas.setPointerCapture(event.pointerId);
      activePointerIdRef.current = event.pointerId;
      rectRef.current = canvas.getBoundingClientRect();
      strokeCountRef.current += 1;
      hasInkRef.current = true;
      setShowPlaceholder(false);

      const point = toCanvasPoint(event);
      pointsRef.current = [point];
      drawDot(context, point);
    }

    function handlePointerMove(event: React.PointerEvent<HTMLCanvasElement>) {
      if (event.pointerId !== activePointerIdRef.current) {
        return;
      }

      event.preventDefault();
      // Gli eventi "coalesced" recuperano i campioni intermedi persi tra due frame.
      const samples = event.nativeEvent.getCoalescedEvents?.() ?? [];
      for (const sample of samples.length > 0 ? samples : [event.nativeEvent]) {
        addPoint(toCanvasPoint(sample));
      }
    }

    function handlePointerEnd(event: React.PointerEvent<HTMLCanvasElement>) {
      const canvas = canvasRef.current;
      const context = contextRef.current;
      if (!canvas || event.pointerId !== activePointerIdRef.current) {
        return;
      }

      const points = pointsRef.current;
      if (context && points.length >= 2) {
        const last = points[points.length - 1];
        const start = midpoint(points[points.length - 2], last);
        context.beginPath();
        context.moveTo(start.x, start.y);
        context.lineTo(last.x, last.y);
        context.stroke();
      }

      activePointerIdRef.current = null;
      rectRef.current = null;
      pointsRef.current = [];
      if (canvas.hasPointerCapture(event.pointerId)) {
        canvas.releasePointerCapture(event.pointerId);
      }
      commitSignature();
    }

    function clear() {
      const canvas = canvasRef.current;
      const context = contextRef.current;
      if (!canvas || !context) {
        return;
      }

      fillBackground(canvas, context);
      strokeCountRef.current += 1;
      activePointerIdRef.current = null;
      hasInkRef.current = false;
      pointsRef.current = [];
      currentDataUrlRef.current = "";
      setShowPlaceholder(true);
      onChange("");
    }

    useImperativeHandle(ref, () => ({
      clear,
      isEmpty: () => !hasInkRef.current,
      toBlob: () =>
        new Promise<Blob>((resolve, reject) => {
          const canvas = canvasRef.current;
          if (!canvas || !hasInkRef.current) {
            reject(new Error("La firma non è disponibile."));
            return;
          }

          canvas.toBlob((blob) => {
            if (!blob) {
              reject(new Error("Impossibile generare la firma."));
              return;
            }

            resolve(blob);
          }, "image/png");
        }),
    }));

    return (
      <div className={`signature-field ${error ? "signature-field--error" : ""}`}>
        <div className="signature-field__header">
          <span className="field__label">{label}</span>
          <button className="ghost-button" type="button" onClick={clear}>
            Cancella firma
          </button>
        </div>
        <div className="signature-pad" ref={containerRef}>
          <canvas
            ref={canvasRef}
            aria-label={label}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerEnd}
            onPointerCancel={handlePointerEnd}
            onLostPointerCapture={handlePointerEnd}
          />
          {showPlaceholder ? (
            <span className="signature-pad__placeholder" aria-hidden="true">
              Firma qui
            </span>
          ) : null}
        </div>
        <p className="signature-field__hint">
          Firma con il dito o con la penna touch nell'area bianca.
        </p>
        {error ? <span className="field__error">{error}</span> : null}
      </div>
    );
  },
);
